import { Delaunay } from 'd3-delaunay';
import type { Rect, Vec } from './node-utils';
import { getNodeCenters } from './node-utils';

type Edge = [string, string];

type RawEdge = { a: number; b: number; dist: number };

/**
 * Extract edges from the Delaunay triangulation (using .halfedges), calculates the "Euclidean distance" for each pair.
 */
const extractEdgesFromDelaunayTriangulation = (delaunay: Delaunay<Delaunay.Point>, centers: Map<string, Vec>, ids: string[]): RawEdge[] => {
    const rawEdges: RawEdge[] = [];

    for (let e = 0; e < delaunay.halfedges.length; e++) {
        const p = delaunay.triangles[e];
        const q = delaunay.halfedges[e] !== -1 ? delaunay.triangles[delaunay.halfedges[e]] : -1;
        if (q === -1 || p === q) continue;

        const cp = centers.get(ids[p]);
        const cq = centers.get(ids[q]);
        if (!cp || !cq) continue;

        const dx = cp.x - cq.x;
        const dy = cp.y - cq.y;
        const dist = Math.hypot(dx, dy);
        rawEdges.push({ a: p, b: q, dist });
    }

    return rawEdges.sort((a, b) => a.dist - b.dist);
};

/**
 * Build a Minimum Spanning Tree on Delaunay triangulation using Prim’s algorithm on edges derived from delaunay.halfedges.
 * This follows the “Growing Tree” algorithm which has strong performance and layout quality compared to earlier methods like PRISM.
 * What happens in this function:
 * 1. It creates the "Delaunay triangulation" of all node centers.
 *    This gives a mesh of “close” neighbors (no three nodes are within the circumcircle of a triangle — ideal for spatial proximity).
 * 2. It then extracts edges from the Delaunay triangulation (using .halfedges), calculates the "Euclidean distance" for each pair.
 * 3. Using those edges, it builds a Minimum Spanning Tree (MST) using Prim’s Algorithm:
 *    • Start with one node.
 *    •	Always connect the nearest unvisited node.
 *    •	This results in a tree where all nodes are connected with minimal total edge length.
 *
 * Why MST?
 * Because resolving overlaps in a spatial graph is most efficient when changes “propagate” through a minimal connection graph.
 * An MST guarantees connectedness with minimal distortion.
 */
const buildMSTfromDelaunay = (nodes: Rect[]): Edge[] => {
    const centers = getNodeCenters(nodes);
    const ids = nodes.map((n) => n.id);
    const points: [number, number][] = ids.map((id) => [centers.get(id)!.x, centers.get(id)!.y]);

    const delaunay = Delaunay.from(points);
    const rawEdges = extractEdgesFromDelaunayTriangulation(delaunay, centers, ids);

    const visited = new Set<number>([0]);
    const result: Edge[] = [];

    while (visited.size < nodes.length) {
        const next = rawEdges.find((e) => visited.has(e.a) !== visited.has(e.b));
        if (!next) break;

        const from = visited.has(next.a) ? next.a : next.b;
        const to = visited.has(next.a) ? next.b : next.a;
        result.push([ids[from], ids[to]]);
        visited.add(to);
    }

    return result;
};

/**
 * Compute vector required to minimally separate two overlapping rectangles (with padding)
 */
const overlapAmount = (a: Rect, b: Rect, padding = 10): Vec | null => {
    const dx = b.x + b.width / 2 - (a.x + a.width / 2);
    const dy = b.y + b.height / 2 - (a.y + a.height / 2);
    const overlapX = a.width / 2 + b.width / 2 + padding - Math.abs(dx);
    const overlapY = a.height / 2 + b.height / 2 + padding - Math.abs(dy);

    if (overlapX > 0 && overlapY > 0) {
        // Move along the smaller overlap axis for minimal distortion
        return overlapX < overlapY ? { x: Math.sign(dx) * overlapX, y: 0 } : { x: 0, y: Math.sign(dy) * overlapY };
    }

    return null;
};

/**
 * Core function: iteratively “grow” the MST (Minimum Spanning Tree) to resolve overlaps.
 * Stops early if no moves occur, bounds iteration count for stability.
 */
export const resolveNodeOverlaps = (nodes: Rect[], padding = 10, maxIter = 10) => {
    const mst = buildMSTfromDelaunay(nodes);
    const nodeMap = new Map(nodes.map((n) => [n.id, n]));
    let moved = true;

    for (let iter = 0; iter < maxIter && moved; iter++) {
        moved = false;

        for (const [idA, idB] of mst) {
            const a = nodeMap.get(idA)!;
            const b = nodeMap.get(idB)!;
            const mv = overlapAmount(a, b, padding);

            if (mv) {
                moved = true;
                a.x -= mv.x / 2;
                a.y -= mv.y / 2;
                b.x += mv.x / 2;
                b.y += mv.y / 2;
            }
        }
    }
};
